import { motion, HTMLMotionProps, Variants } from 'framer-motion';
import { ReactNode } from 'react';
import { slideUp, slideDown, slideLeft, slideRight } from '@/lib/motion/variants';
import { useReducedMotion } from '@/lib/motion/hooks/use-reduced-motion';

type Direction = 'up' | 'down' | 'left' | 'right';

const directionVariants: Record<Direction, Variants> = {
  up: slideUp,
  down: slideDown,
  left: slideLeft,
  right: slideRight,
};

interface SlideInProps extends Omit<HTMLMotionProps<'div'>, 'children' | 'viewport'> {
  children: ReactNode;
  direction?: Direction;
  delay?: number;
  duration?: number;
  className?: string;
  /** Whether to animate when element enters viewport */
  viewport?: boolean;
}

export function SlideIn({
  children,
  direction = 'up',
  delay = 0,
  duration,
  className,
  viewport = false,
  ...props
}: SlideInProps) {
  const reducedMotion = useReducedMotion();

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const variants = directionVariants[direction];

  const animateProps = viewport
    ? {
        initial: 'hidden',
        whileInView: 'visible',
        viewport: { once: true, margin: '-50px' },
      }
    : {
        initial: 'hidden',
        animate: 'visible',
      };

  return (
    <motion.div
      variants={variants}
      {...animateProps}
      exit="exit"
      transition={{ delay, ...(duration && { duration }) }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}
